<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
defineProps({
  selectedOfferSteps: { type: Array, default: () => [] },
  selectedOfferProgressAlert: { type: Object, default: null },
});

const emit = defineEmits(['stepAction']);

const stepStateLabel = step =>
  ({
    done: 'Concluído',
    current: 'Em andamento',
    error: 'Requer atenção',
    pending: 'Pendente',
  })[step?.state] || 'Pendente';

// A blocked CTA stays visible with its reason instead of disappearing, so the
// operator can see *why* the step cannot advance (FE-AUD-74: no dead paths).
const handleProgressStepAction = (step, action) => {
  if (action?.disabled) return;
  emit('stepAction', step, action);
};

const blockedActions = step =>
  (step?.actions || []).filter(
    action => action?.disabled && (action.hint || action.blockReason)
  );
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="min-w-0 rounded-lais border border-lais-border bg-white p-4">
    <h3 class="font-redHatDisplay text-[16px] font-semibold text-lais-text">
      Acompanhe o progresso
    </h3>
    <ol class="mt-3 grid gap-3">
      <li
        v-for="step in selectedOfferSteps"
        :key="step.key || step.label"
        class="flex min-w-0 items-start gap-3 text-[13px]"
        :aria-current="step.state === 'current' ? 'step' : undefined"
      >
        <span
          class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border text-[11px]"
          aria-hidden="true"
          :class="
            step.state === 'done'
              ? 'border-lais-primary bg-lais-primary text-white'
              : step.state === 'current'
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : step.state === 'error'
                  ? 'border-red-200 bg-red-50 text-red-600'
                  : 'border-lais-border bg-white text-lais-text-muted'
          "
        >
          <span
            v-if="step.state === 'done'"
            class="i-lucide-check size-3"
            aria-hidden="true"
          />
          <span
            v-else-if="step.state === 'error'"
            class="i-lucide-x size-3"
            aria-hidden="true"
          />
        </span>
        <span
          class="min-w-0 break-words"
          :class="
            step.state === 'pending'
              ? 'text-lais-text-muted'
              : 'font-semibold text-lais-text'
          "
        >
          <span class="sr-only">Estado: {{ stepStateLabel(step) }}. </span>
          {{ step.label }}
          <span
            v-if="step.description"
            class="mt-0.5 block break-words text-[12px] font-normal leading-relaxed text-lais-text-muted"
          >
            {{ step.description }}
          </span>
          <span
            v-if="step.actions?.length"
            class="mt-2 flex min-w-0 flex-col gap-1.5"
          >
            <span class="flex flex-wrap gap-2">
              <button
                v-for="action in step.actions"
                :key="action.key"
                type="button"
                :disabled="Boolean(action.disabled)"
                :title="action.hint || action.blockReason || undefined"
                :aria-label="
                  action.disabled && (action.hint || action.blockReason)
                    ? `${action.label}. ${action.hint || action.blockReason}`
                    : action.label
                "
                class="inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-lais border px-2.5 py-1 text-[12px] font-semibold transition"
                :class="[
                  action.disabled
                    ? 'cursor-not-allowed border-lais-border bg-lais-bg-soft text-lais-text-muted'
                    : action.variant === 'primary'
                      ? 'border-lais-primary bg-lais-primary text-white hover:bg-lais-primary/90'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary hover:text-lais-primary',
                ]"
                @click="handleProgressStepAction(step, action)"
              >
                <span class="size-3.5" :class="action.icon" />
                <span class="min-w-0 break-words whitespace-normal">
                  {{ action.label }}
                </span>
              </button>
            </span>
            <span
              v-for="action in blockedActions(step)"
              :key="`${action.key}-reason`"
              class="break-words text-[12px] font-normal leading-relaxed text-lais-text-muted"
            >
              {{ action.hint || action.blockReason }}
            </span>
          </span>
        </span>
      </li>
    </ol>
    <div
      v-if="selectedOfferProgressAlert"
      class="mt-4 rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
    >
      <p class="break-words font-semibold text-lais-text">
        {{ selectedOfferProgressAlert.title }}
      </p>
      <p class="mt-1 break-words">
        {{ selectedOfferProgressAlert.description }}
      </p>
    </div>
  </section>
</template>
